import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Form, Row, Col, Input, List, Pagination, Button, Typography, Select, Alert } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import noImage from '../../assets/img/no-image.png';
import { fetchMakes } from '../../store/actions/vehicleMakes-actions';
import { fetchModelsByMake } from '../../store/actions/vehicleModels-actions';

const CompleteRequestModal = ({
  open,
  onCancel,
  onOk,
  okDisabled,
  t,
  completeForm,
  vehicleFilter,
  onVehicleFilterChange,
  onVehicleFiltersClear,
  onVehicleFiltersSearch,
  vehiclesState,
  selectedVehicleId,
  setSelectedVehicleId,
  vehiclePage,
  onVehiclePageChange,
  currentRequest,
}) => {
  const dispatch = useDispatch();
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedMake, setSelectedMake] = useState(null);

  useEffect(() => {
    if (!open) return;
    dispatch(fetchMakes())
      .then((res) => setMakes(res?.payload || []))
      .catch(() => {});
  }, [open, dispatch]);

  useEffect(() => {
    if (!selectedMake?.id) {
      setModels([]);
      return;
    }
    dispatch(fetchModelsByMake({ vehicleMakeId: selectedMake.id }))
      .then((res) => setModels(res?.payload || []))
      .catch(() => {});
  }, [dispatch, selectedMake]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={t('imageRequests.modal.completeTitle')}
      onOk={onOk}
      okText={t('imageRequests.actions.complete')}
      okButtonProps={{ disabled: okDisabled }}
      width={900}
    >
      <Alert type="info" showIcon message={t('imageRequests.modal.autoUploadInfo')} style={{ marginBottom: 12 }} />
      <Form form={completeForm} layout="vertical">
        <Row gutter={8} align="bottom">
          <Col span={8}>
            <Form.Item label={t('imageRequests.filters.vin')} style={{ marginBottom: 8 }}>
              <Input
                allowClear
                placeholder={t('imageRequests.filters.vin')}
                value={vehicleFilter.vin}
                onChange={(e) => onVehicleFilterChange('vin', e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('imageRequests.filters.make')} style={{ marginBottom: 8 }}>
              <Select
                allowClear
                showSearch
                placeholder={t('imageRequests.filters.make')}
                value={selectedMake?.id || null}
                onChange={(makeId) => {
                  const m = makes.find((mk) => mk.id === makeId) || null;
                  console.log(m)
                  setSelectedMake(m);
                  onVehicleFilterChange('make', m ? m.name : '');
                }}
                filterOption={(input, option) => (option?.children || '').toLowerCase().includes(input.toLowerCase())}
              >
                {makes.map((mk) => (
                  <Select.Option key={mk.id} value={mk.id}>
                    {mk.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('imageRequests.filters.model')} style={{ marginBottom: 8 }}>
              <Select
                allowClear
                showSearch
                placeholder={t('imageRequests.filters.model')}
                value={vehicleFilter.model || null}
                onChange={(val) => onVehicleFilterChange('model', val || '')}
                disabled={!selectedMake}
                filterOption={(input, option) => (option?.children || '').toLowerCase().includes(input.toLowerCase())}
              >
                {models.map((md) => (
                  <Select.Option key={md.id} value={md.name}>
                    {md.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 3, marginBottom: 5 }}>
            <Button size="small" type="primary" shape="round" icon={<SearchOutlined />} onClick={onVehicleFiltersSearch}>
              {t('common.search')}
            </Button>
            <Button type="default" shape="round" icon={<ReloadOutlined />} size="small" onClick={onVehicleFiltersClear}>
              {t('common.reset')}
            </Button>
          </Col>
        </Row>

        <Form.Item name="vehicleId" rules={[{ required: true, message: t('vehicles.selectUser') }]}>
          <div>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              {t('imageRequests.modal.selectVehicle')}
            </Typography.Text>
            <List
              grid={{ gutter: 8, xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }}
              loading={vehiclesState.isLoading}
              dataSource={vehiclesState.vehicles || []}
              renderItem={(v) => {
                const make = v.vehicleMake?.name || v.make || '';
                const model = v.vehicleModel?.name || v.model || '';
                const vin = (v.vin || '').trim();
                const img = (v.images && v.images[0]?.path) || noImage;
                const isSelected = selectedVehicleId === v.id;
                const reqVin = (currentRequest?.vin || '').replace(/\s+/g, '').toUpperCase();
                const itemVin = (vin || '').replace(/\s+/g, '').toUpperCase();
                const vinMatch = reqVin && itemVin && reqVin === itemVin;
                return (
                  <List.Item key={v.id}>
                    <div
                      onClick={() => {
                        setSelectedVehicleId(v.id);
                        completeForm.setFieldsValue({ vehicleId: v.id });
                      }}
                      style={{ cursor: 'pointer', border: isSelected ? '2px solid #3d996f' : '1px solid #f0f0f0', borderRadius: 6, padding: 8, display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <img src={img} alt="thumb" style={{ width: 60, height: 44, objectFit: 'cover', borderRadius: 4 }} />
                      <div>
                        <div style={{ fontWeight: 500 }}>{`${make} ${model}`.trim() || 'N/A'}</div>
                        <div style={{ color: vinMatch ? '#3d996f' : '#999', fontSize: 12, fontWeight: vinMatch ? 600 : 400 }}>{vin || '-'}</div>
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <Pagination current={vehiclePage} pageSize={8} total={vehiclesState.count || 0} showSizeChanger={false} onChange={onVehiclePageChange} size="small" />
            </div>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CompleteRequestModal;

